import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePlatformDashboardSelector } from 'platform-dashboard-ui/store';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import FormattedMessage from 'I18n/components/FormattedMessage';
import H5 from 'UIComponents/elements/headings/H5';
import UIButton from 'UIComponents/button/UIButton';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getDefaultOperatorForProperty } from 'platform-dashboard-ui/filter/utils/dataSourceHelpers';
import { selectDataSourcesMetadataWithUniqueLabels } from 'platform-dashboard-ui/ducks/dataSourceMetadataSlice';
import { convertSingleOperatorToGroupOperators } from '../../../../../lib/dashboardFilters/filterConversionHelpers';
import { addGroupDashboardFilter } from '../../../../../ducks/applied-dashboard-filters';
// @ts-expect-error migrate upstream dependency
import { unpinQuickFilterGroup } from '../../../../../ducks/quick-filters/quickFilters';
import { useQuickFilterGroupNameAndDesc } from '../hooks/useQuickFilterGroupNameAndDesc';

// @ts-expect-error migrate upstream dependency
import { trackQuickFilterGroupInteraction } from '../../../../../../tracking/DashboardUITracker';
import { AsyncQuickFilterGroupFilterOperatorCRMInput as QuickFilterGroupFilterOperatorCRMInput } from 'platform-dashboard-ui/components/quickFilterGroup/filter/AsyncQuickFilterGroupFilterOperatorCRMInput';
import { validateFilterOperator } from 'platform-dashboard-ui/filter/utils/filterEditorUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const GroupedPinnedPropertyOperatorCRMInput = props => {
  const {
    quickFilterGroup,
    prevOperator,
    dashboardId,
    userHasQuickFilterEditPermission,
    onApplyGroupFilter,
    userRole
  } = props;
  const {
    properties,
    systemGenerated,
    name: originalQuickFilterGroupName
  } = quickFilterGroup;
  const {
    name,
    description
  } = useQuickFilterGroupNameAndDesc(quickFilterGroup);
  const dispatch = useDispatch();
  const quickFilterGroupDataSources = properties.map(property => {
    return property.dataSource;
  });
  const dataSourcesMetadata = usePlatformDashboardSelector(selectDataSourcesMetadataWithUniqueLabels(quickFilterGroupDataSources));

  // Since all properties in a quick filter group have the same data type, possible operators and values,
  // we derive the operator input logic from the first property available.
  const firstProperty = properties.at(0);
  const firstPropertyDataSource = firstProperty['dataSource'];
  const fullProperty = firstProperty['property'];
  const [selectedOperator, setSelectedOperator] = useState(prevOperator || getDefaultOperatorForProperty(firstPropertyDataSource, fullProperty));
  const {
    isValid,
    message: operatorValidationMessage
  } = validateFilterOperator(selectedOperator);
  const handleApplyGroupedFilter = () => {
    const quickFilterGroupOperators = convertSingleOperatorToGroupOperators({
      singlePropertyOperator: selectedOperator,
      quickFilterGroupProperties: properties,
      dataSourcesMetadata
    });
    const payload = {
      dashboardId,
      quickFilterGroupOperators,
      systemGenerated,
      quickFilterGroupName: originalQuickFilterGroupName
    };
    dispatch(addGroupDashboardFilter(payload));
    trackQuickFilterGroupInteraction({
      action: 'add-grouped-filter',
      quickFilterGroupData: {
        quickFilterGroup,
        operator: selectedOperator
      },
      userRole,
      additionalProps: {
        interactionSource: 'quick-filter-popover'
      }
    });
    onApplyGroupFilter();
  };
  const handleUnpinGroupFilter = () => {
    dispatch(unpinQuickFilterGroup(quickFilterGroup));
    trackQuickFilterGroupInteraction({
      action: 'unpin-grouped-filter',
      quickFilterGroupData: {
        quickFilterGroup
      },
      userRole,
      additionalProps: {
        interactionSource: 'quick-filter-popover'
      }
    });
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(ScrollContainer, {
      maxHeight: "100%",
      children: [/*#__PURE__*/_jsxs(H5, {
        className: "m-bottom-0",
        children: [systemGenerated && /*#__PURE__*/_jsx(UIIcon, {
          name: "dynamicFilter"
        }), " ", name]
      }), /*#__PURE__*/_jsx(UIExpandableText, {
        className: "m-bottom-3",
        children: description
      })]
    }), /*#__PURE__*/_jsx(QuickFilterGroupFilterOperatorCRMInput, {
      groupDefinedProperty: fullProperty,
      groupDefinedPropertyDataSourceId: firstPropertyDataSource,
      selectedOperator: selectedOperator,
      onSelectOperator: setSelectedOperator
    }), /*#__PURE__*/_jsxs(ButtonWrapper, {
      justify: "between",
      paddingInline: "0",
      paddingBlock: "400",
      children: [/*#__PURE__*/_jsx(UITooltip, {
        title: operatorValidationMessage,
        children: /*#__PURE__*/_jsx(UIButton, {
          disabled: !isValid,
          "data-test-id": "apply-filter-button",
          use: "tertiary",
          size: "small",
          onClick: handleApplyGroupedFilter,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "quickFilter.apply"
          })
        })
      }), /*#__PURE__*/_jsx(UITooltip, {
        disabled: userHasQuickFilterEditPermission,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "quickFilter.permissionConfig",
          options: {
            action: 'unpin'
          }
        }),
        children: /*#__PURE__*/_jsxs(UIButton, {
          use: "transparent",
          size: "small",
          disabled: !userHasQuickFilterEditPermission,
          "data-test-id": "popover-unpin-quick-filter-button",
          onClick: handleUnpinGroupFilter,
          children: [/*#__PURE__*/_jsx(UIIcon, {
            name: "delete"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "quickFilter.unpin.title"
          })]
        })
      })]
    })]
  });
};